// Generic onSnapshot hook with loading / error state.
// Pass a typed Query (e.g. usersCol() or query(audiosCol(), where(...))).
//
// Resilient subscribe (activity-day hardening): the SDK silently recovers
// transient blips on its own — when our onError callback actually fires,
// the listener is terminal and must be re-subscribed by hand or it stays
// dead until a page reload. So on error we (1) keep the last-good data
// instead of blanking the screen, and (2) re-subscribe with exponential
// backoff (1s→2s→…→30s cap). permission-denied is NOT retried (retrying
// can't fix it, and it would hammer Rules); we surface it and stop, which
// also preserves useEventConfig's fail-open (error stays set → learner is
// let through, not locked out of the countdown gate).
//
// Foreground re-sync: a backgrounded tab (worker switches apps mid-event,
// phone locks) can have its listener frozen so a doc written while away
// never lands — the worker then sees a stale count (e.g. "2 echoes" when
// a 3rd was uploaded). On visibilitychange→visible we tear down and re-
// subscribe, forcing one fresh read. permission-denied stays terminal
// (don't re-arm a listener Rules will just reject again).

import {
  type Query,
  onSnapshot,
  type DocumentData,
  type FirestoreError,
} from "firebase/firestore";
import { useEffect, useState } from "react";

/** Document with its Firestore docId merged in as `id`. */
export type WithId<T> = T & { id: string };

export interface CollectionState<T> {
  data: WithId<T>[];
  loading: boolean;
  error: Error | null;
}

const MAX_BACKOFF_MS = 30_000;

export function useFirestoreCollection<T extends DocumentData>(
  query: Query<T> | null,
): CollectionState<T> {
  const [state, setState] = useState<CollectionState<T>>({
    data: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!query) {
      setState({ data: [], loading: false, error: null });
      return;
    }

    let cancelled = false;
    let unsub: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    // Set once Rules reject us — keeps the foreground re-sync from re-arming
    // a listener that will only be denied again (and hammer Rules).
    let denied = false;

    const subscribe = () => {
      if (cancelled) return;
      unsub = onSnapshot(
        query,
        (snap) => {
          if (cancelled) return;
          attempt = 0; // recovered → reset backoff
          const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setState({ data, loading: false, error: null });
        },
        (err) => {
          if (cancelled) return;
          // Keep last-good data so a weak-signal blip doesn't blank the UI.
          setState((prev) => ({ data: prev.data, loading: false, error: err }));
          unsub?.();
          unsub = null;
          // permission-denied won't recover by retrying — stop here.
          if ((err as FirestoreError).code === "permission-denied") {
            denied = true;
            return;
          }
          const delay = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
          attempt += 1;
          retryTimer = setTimeout(subscribe, delay);
        },
      );
    };

    // Tear down the current listener (and any pending backoff retry) and open
    // a fresh one — forces a server read after the tab returns to foreground.
    const resubscribe = () => {
      if (cancelled || denied) return;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      attempt = 0;
      unsub?.();
      unsub = null;
      subscribe();
    };

    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        resubscribe();
      }
    };

    subscribe();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }

    return () => {
      cancelled = true;
      unsub?.();
      if (retryTimer) clearTimeout(retryTimer);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
    };
  }, [query]);

  return state;
}
